"use client"

import { useEffect, useState } from "react"
import { SendMark } from "@/components/ui/send-mark"
import { useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { JobInquireModal } from "@/components/hiring/job-inquire-modal"
import { AuthModal } from "@/components/auth/auth-modal"
import { useUserRole } from "@/providers/user-role-provider"
import { trackMeta } from "@/lib/meta-pixel"
import { trackTikTok } from "@/lib/tiktok-pixel"

// Auth-gated trigger for the JobInquireModal.
//   • Logged out → opens AuthModal (signup), continues to inquire on success.
//   • Logged in as the shop owner → disabled, shows '(your post)' note.
//   • Logged in as anyone else → opens the inquire modal directly.

interface JobInquireButtonProps {
  listingId: string
  listingTitle: string
  shopId: string
  shopName: string
  ownerId: string
  className?: string
  size?: "default" | "lg"
  // 'pill' = filled black CTA. 'text' = bare icon+label in emerald-700,
  // matching the talent profile's Message/Inquire row.
  variant?: "pill" | "text"
}

export function JobInquireButton({
  listingId, listingTitle, shopId, shopName, ownerId, className = "", size = "default", variant = "pill",
}: JobInquireButtonProps) {
  const router = useRouter()
  const { role } = useUserRole()
  const [authOpen, setAuthOpen] = useState(false)
  const [inquireOpen, setInquireOpen] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [currentUserName, setCurrentUserName] = useState<string>("a barber")

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(async ({ data }: { data: { user: { id: string } | null } }) => {
      if (!data.user) return
      setCurrentUserId(data.user.id)
      const { data: prof } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", data.user.id)
        .maybeSingle()
      if (prof?.full_name) setCurrentUserName(prof.full_name)
    }).catch(() => {})
  }, [])

  const isOwnPost = currentUserId === ownerId

  const handleClick = () => {
    if (!currentUserId) {
      setAuthOpen(true)
      return
    }
    if (isOwnPost) return
    setInquireOpen(true)
  }

  const sizeClass = size === "lg" ? "h-11 px-6 text-sm" : "h-9 px-5 text-xs"
  // The filled CTA is bg-foreground/text-background rather than a literal
  // black-on-white: in dark mode the hard-coded black sat on a near-black
  // panel and the button all but disappeared.

  // Owner-mode users can't inquire on hiring postings — they're shop
  // owners themselves, not barbers looking for a chair. Hide the CTA
  // entirely instead of showing a disabled button.
  if (role === "owner") return null

  // 'text' variant — bare emerald link with Send icon, matches the
  // Message/Inquire row on the talent profile. Disabled (own post) state
  // dims the label and swaps to a neutral 'Your post' note.
  if (variant === "text") {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled={isOwnPost}
          aria-label="Inquire"
          className={`inline-flex items-center gap-1.5 text-xs md:text-sm font-light transition-opacity ${
            isOwnPost
              ? "text-gray-400 cursor-not-allowed"
              : "text-booth-green hover:opacity-70"
          } ${className}`}
        >
          <SendMark size={14} className="md:h-4 md:w-4" />
          {isOwnPost ? "Your post" : "Inquire"}
        </button>

        {currentUserId && !isOwnPost && (
          <JobInquireModal
            open={inquireOpen}
            onClose={() => setInquireOpen(false)}
            listingId={listingId}
            listingTitle={listingTitle}
            shopId={shopId}
            shopName={shopName}
            ownerId={ownerId}
            barberId={currentUserId}
            barberName={currentUserName}
            onSent={() => {
            // Mid-funnel conversion for the hiring ads — a barber actually
            // reached out about a posting.
            trackMeta("Lead", { content_type: "hiring_post", content_ids: listingId })
            trackTikTok("SubmitForm", { content_type: "product", content_id: listingId })
            router.refresh()
          }}
          />
        )}
        <AuthModal
          open={authOpen}
          onOpenChange={(o) => { if (!o) setAuthOpen(false) }}
          defaultMode="signup"
        />
      </>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isOwnPost}
        className={`inline-flex items-center justify-center gap-1.5 ${sizeClass} rounded-full bg-foreground text-background font-semibold tracking-wide shadow-sm ring-1 ring-inset ring-foreground/10 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity ${className}`}
      >
        <SendMark size={14} />
        {isOwnPost ? "Your post" : "Inquire"}
      </button>

      {/* Inquire modal — only when authenticated and not the owner */}
      {currentUserId && !isOwnPost && (
        <JobInquireModal
          open={inquireOpen}
          onClose={() => setInquireOpen(false)}
          listingId={listingId}
          listingTitle={listingTitle}
          shopId={shopId}
          shopName={shopName}
          ownerId={ownerId}
          barberId={currentUserId}
          barberName={currentUserName}
          onSent={() => {
            // Mid-funnel conversion for the hiring ads — a barber actually
            // reached out about a posting.
            trackMeta("Lead", { content_type: "hiring_post", content_ids: listingId })
            trackTikTok("SubmitForm", { content_type: "product", content_id: listingId })
            router.refresh()
          }}
        />
      )}

      {/* Auth wall for logged-out users */}
      <AuthModal
        open={authOpen}
        onOpenChange={(o) => { if (!o) setAuthOpen(false) }}
        defaultMode="signup"
      />
    </>
  )
}
